import type { Point, Quad } from './model';
/** Row-major 3×3: [a b c; d e f; g h i]. Maps (u, v, 1) → (x, y, w) with x/w, y/w. */
export type Mat3 = Float64Array;
/** One degeneracy threshold for the whole module: a quad whose 3×3 determinant (or whose
 *  square-to-quad system) falls below this is treated as degenerate rather than inverted. */
const EPS = 1e-9;
/** Heckbert's square-to-quad: (0,0)→tl, (1,0)→tr, (1,1)→br, (0,1)→bl. */
export function squareToQuad(q: Quad): Mat3 {
  const [x0, y0] = q.tl, [x1, y1] = q.tr, [x2, y2] = q.br, [x3, y3] = q.bl;
  const sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
  const m = new Float64Array(9);
  if (Math.abs(sx) < EPS && Math.abs(sy) < EPS) {
    m.set([x1 - x0, x3 - x0, x0, y1 - y0, y3 - y0, y0, 0, 0, 1]);
  } else {
    const dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2;
    const det = dx1 * dy2 - dx2 * dy1;
    if (Math.abs(det) < EPS) throw new Error('homography: degenerate quad');
    const g = (sx * dy2 - dx2 * sy) / det, h = (dx1 * sy - sx * dy1) / det;
    m.set([x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h, 1]);
  }
  if (Math.abs(det3(m)) < EPS) throw new Error('homography: degenerate quad');
  return m;
}
function det3(m: Mat3): number {
  return m[0] * (m[4] * m[8] - m[5] * m[7]) - m[1] * (m[3] * m[8] - m[5] * m[6]) + m[2] * (m[3] * m[7] - m[4] * m[6]);
}
export function invert3(m: Mat3): Mat3 {
  const d = det3(m);
  if (Math.abs(d) < EPS) throw new Error('homography: singular matrix');
  const o = new Float64Array(9);
  o[0] = (m[4] * m[8] - m[5] * m[7]) / d; o[1] = (m[2] * m[7] - m[1] * m[8]) / d; o[2] = (m[1] * m[5] - m[2] * m[4]) / d;
  o[3] = (m[5] * m[6] - m[3] * m[8]) / d; o[4] = (m[0] * m[8] - m[2] * m[6]) / d; o[5] = (m[2] * m[3] - m[0] * m[5]) / d;
  o[6] = (m[3] * m[7] - m[4] * m[6]) / d; o[7] = (m[1] * m[6] - m[0] * m[7]) / d; o[8] = (m[0] * m[4] - m[1] * m[3]) / d;
  return o;
}
export function applyH(m: Mat3, x: number, y: number): Point {
  const w = m[6] * x + m[7] * y + m[8];
  // w → 0 is the vanishing line: the point maps to infinity. Return NaN so every range test
  // (`insideUnit`, the compositor's u/v checks) rejects it: the bbox loops in shading.ts and
  // template.ts scan pixels outside a keystoned quad, where the vanishing line can lie, and a
  // throw there would abort a whole template preparation for one unreachable pixel.
  if (Math.abs(w) < 1e-12) return [NaN, NaN];
  return [(m[0] * x + m[1] * y + m[2]) / w, (m[3] * x + m[4] * y + m[5]) / w];
}
export function quadToSquare(q: Quad): Mat3 { return invert3(squareToQuad(q)); }
export function insideUnit(u: number, v: number): boolean { return u >= 0 && u <= 1 && v >= 0 && v <= 1; }
export function quadBounds(q: Quad): { x0: number; y0: number; x1: number; y1: number } {
  const xs = [q.tl[0], q.tr[0], q.br[0], q.bl[0]], ys = [q.tl[1], q.tr[1], q.br[1], q.bl[1]];
  return { x0: Math.floor(Math.min(...xs)), y0: Math.floor(Math.min(...ys)), x1: Math.ceil(Math.max(...xs)), y1: Math.ceil(Math.max(...ys)) };
}
